import { motion } from "framer-motion";
import { BadgeCheck, Award, ShieldHalf, Timer } from "lucide-react";
import { useLang } from "@/lib/i18n";

const ICONS = [BadgeCheck, Award, ShieldHalf, Timer];

export default function About() {
  const { t } = useLang();
  const HIGHLIGHTS = ICONS.map((icon, i) => ({ icon, text: t("about.highlights")[i] }));
  return (
    <section id="about" data-testid="about-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="absolute -inset-8 bg-accent/10 blur-3xl rounded-full -z-10" aria-hidden="true" />
          <div className="relative border border-black bg-white p-3">
            <span className="absolute -top-px -left-px w-6 h-6 border-t-2 border-l-2 border-primary" aria-hidden="true" />
            <span className="absolute -bottom-px -right-px w-6 h-6 border-b-2 border-r-2 border-primary" aria-hidden="true" />
            <img
              src="/hamdou-portrait.jpeg"
              alt="Hamdou Wane, Principal Consultant at ZTrust Africa"
              loading="lazy"
              data-testid="about-portrait"
              className="w-full h-[380px] sm:h-[460px] object-cover object-[30%_20%]"
            />
            <div className="flex items-center justify-between px-2 py-3">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Cincinnati, OH</p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-primary">{t("about.available")}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-4">{t("about.overline")}</p>
          <h2 className="font-heading font-bold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mb-8">
            {t("about.h1a")}<br />{t("about.h1b")}<span className="text-accent">.</span>
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-xl mb-5">
            {t("about.bio1")}
          </p>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-xl mb-10">
            {t("about.bio2")}
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl" data-testid="about-highlights">
            {HIGHLIGHTS.map((h) => (
              <li key={h.text} className="flex items-center gap-3 border border-black bg-white px-4 py-3">
                <h.icon className="w-4 h-4 text-accent shrink-0" strokeWidth={1.75} />
                <span className="text-sm text-foreground">{h.text}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}
